import { useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { PHONE_MASK, PHONE_PLACEHOLDER } from '@/core/format';
import { PersonSearchMode, type PersonSearchModeValue } from '../PersonSearchMode/PersonSearchMode';
import classes from './PersonSearchFilter.module.scss';

interface PersonSearchFilterProps {
    name: string;
    label: string;
    namePlaceholder: string;
    nameValue: string;
    phoneValue: string;
    error?: string;
    onChange: (mode: PersonSearchModeValue, value: string) => void;
}

/** Вводит текстовый фильтр по имени или телефону без выбора человека */
export const PersonSearchFilter = (
    {
        name,
        label,
        namePlaceholder,
        nameValue,
        phoneValue,
        error,
        onChange,
    }: PersonSearchFilterProps
) => {
    const [emptyMode, setEmptyMode] = useState<PersonSearchModeValue>(phoneValue
        ? 'phone'
        : 'name');

    const mode = phoneValue
        ? 'phone'
        : nameValue
            ? 'name'
            : emptyMode;

    const savedValues = useRef({
        name: nameValue,
        phone: phoneValue
    });

    /** Переключает способ поиска и возвращает ранее введённое значение */
    const changeMode = (nextMode: PersonSearchModeValue) => {
        if (nextMode === mode) return;

        savedValues.current[mode] = mode === 'name'
            ? nameValue
            : phoneValue;
        setEmptyMode(nextMode);
        onChange(nextMode, savedValues.current[nextMode]);
    };

    /** Передаёт введённый текст в текущий фильтр */
    const changeValue = (value: string) => {
        savedValues.current[mode] = value;
        setEmptyMode(mode);
        onChange(mode, value);
    };

    return <div
        className={classes.field}
        role="group"
        aria-label={label}
    >
        <label
            className={classes.label}
            htmlFor={name}
        >
            {label}
        </label>
        <div className={classes.control}>
            {mode === 'phone'
                ? (
                    <InputMask
                        id={name}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error
                            ? `${name}-error`
                            : undefined
                        }
                        name={name}
                        value={phoneValue}
                        onChange={(event) => changeValue(event.value ?? '')}
                        mask={PHONE_MASK}
                        autoClear={false}
                        placeholder={PHONE_PLACEHOLDER}
                        inputMode="tel"
                        className={classes.input}
                    />
                )
                : (
                    <InputText
                        id={name}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error
                            ? `${name}-error`
                            : undefined
                        }
                        name={name}
                        value={nameValue}
                        onChange={(event) => changeValue(event.target.value)}
                        placeholder={namePlaceholder}
                        className={classes.input}
                    />
                )}
            <PersonSearchMode
                mode={mode}
                onChange={changeMode}
                compact
            />
        </div>
        {error &&
            <small
                id={`${name}-error`}
                className={classes.error}
                role="alert"
            >
                {error}
            </small>
        }
    </div>;
};
